import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';

interface AuditEntry {
  id: number;
  entity_type: 'event' | 'task';
  entity_id: number;
  action: 'create' | 'update' | 'delete' | 'toggle_done';
  actor_id: number;
  actor_name: string;
  actor_color: string | null;
  brand_id: number | null;
  payload: { name?: string; title?: string } | null;
  created_at: string;
}

const actionVerb: Record<AuditEntry['action'], string> = {
  create: 'criou',
  update: 'editou',
  delete: 'excluiu',
  toggle_done: 'atualizou',
};

function notify(e: AuditEntry): void {
  const subject =
    e.payload?.name ??
    e.payload?.title ??
    `${e.entity_type === 'event' ? 'evento' : 'tarefa'} #${e.entity_id}`;
  toast.info(`${e.actor_name} ${actionVerb[e.action]}: ${subject}`);

  if (document.visibilityState !== 'visible' && Notification.permission === 'granted') {
    void navigator.serviceWorker.ready.then((reg) => {
      void reg.showNotification(`${e.actor_name} ${actionVerb[e.action]}`, {
        body: subject,
        icon: '/icons/icon-192.png',
        badge: '/icons/badge-96.png',
        tag: 'radar-' + e.id,
      });
    });
  }
}

/**
 * Radar de equipe em tempo quase-real via SSE (`/api/v1/stream`).
 * O backend já filtra eco do próprio autor da ação.
 */
export function useAuditStream(): void {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  useEffect(() => {
    if (!user) return;

    const base = import.meta.env.VITE_API_URL ?? '/api/v1';
    const source = new EventSource(`${base}/stream`, { withCredentials: true });

    source.addEventListener('audit', (ev) => {
      const entry = JSON.parse((ev as MessageEvent<string>).data) as AuditEntry;
      notify(entry);
      void qc.invalidateQueries({ queryKey: ['events'] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    });

    // Erros de conexão (rede, deploy) fazem o EventSource reconectar sozinho — nada a fazer aqui.
    source.onerror = () => {};

    return () => source.close();
  }, [user, qc]);
}
